import { getCurrentUser } from '@/lib/auth-utils'
import { fetchAccessibleCompanyIds } from '@/lib/company-access'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { Announcement } from '@/models'
import { neon } from '@/services/neon'

export async function fetchAnnouncements(companyId?: string): Promise<Announcement[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  let query = neon
    .from('announcements')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (user.role === ROLE_ADMIN) {
    if (companyId) {
      query = query.eq('company_id', companyId)
    }
  } else {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (companyIds.length === 0) {
      return []
    }
    if (companyId) {
      if (!companyIds.includes(companyId)) {
        return []
      }
      query = query.eq('company_id', companyId)
    } else {
      query = query.in('company_id', companyIds)
    }
  }

  const { data, error } = await query
  if (error) {
    throw new Error(error.message)
  }
  return (data as Announcement[]) || []
}

export async function fetchActiveAnnouncements(companyId: string): Promise<Announcement[]> {
  const { data, error } = await neon
    .from('announcements')
    .select('id,title,content,background_color')
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .eq('is_active', true)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(error.message)
  }
  return (data as Announcement[]) || []
}

export async function fetchActiveSiteAnnouncements(): Promise<Announcement[]> {
  const { data, error } = await neon
    .from('announcements')
    .select('id,title,content,background_color')
    .eq('scope', 'site')
    .is('deleted_at', null)
    .eq('is_active', true)
    .order('created_at', { ascending: false })

  if (error) throw new Error(error.message)
  return (data as Announcement[]) || []
}

export async function fetchAnnouncementById(id: string): Promise<Announcement> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const { data, error } = await neon
    .from('announcements')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()
  if (error) {
    throw new Error(error.message)
  }

  const announcement = data as Announcement
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!announcement.company_id || !companyIds.includes(announcement.company_id)) {
      throw new Error('You do not have permission to view this announcement.')
    }
  }

  return announcement
}

export async function createAnnouncement(payload: Partial<Announcement>): Promise<Announcement> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  if (user.role !== ROLE_ADMIN) {
    if (!payload.company_id) {
      throw new Error('Company ID is required.')
    }
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!companyIds.includes(payload.company_id)) {
      throw new Error('You do not have permission to create announcements for this company.')
    }
  }

  const { data, error } = await neon
    .from('announcements')
    .insert({ ...payload, created_by: user.id })
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }
  return data as Announcement
}

export async function updateAnnouncement(
  id: string,
  payload: Partial<Announcement>,
): Promise<Announcement> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const existing = await fetchAnnouncementById(id)
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!existing.company_id || !companyIds.includes(existing.company_id)) {
      throw new Error('You do not have permission to update this announcement.')
    }
  }

  const { data, error } = await neon
    .from('announcements')
    .update(payload)
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) throw new Error('Announcement not found or permission revoked.')
    throw new Error(error.message)
  }
  return data as Announcement
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const existing = await fetchAnnouncementById(id)
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!existing.company_id || !companyIds.includes(existing.company_id)) {
      throw new Error('You do not have permission to delete this announcement.')
    }
    if (user.role !== 'owner') {
      throw new Error('Only owners and admins can delete announcements.')
    }
  }

  const { error, data } = await (neon.from('announcements') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return
    throw new Error(error.message)
  }
  if (!data) return
}
